/*
 * Tracking Event Editor - Event Report Filing Dialog
 * Angular Module: trackerbase-app.views.tracking-event-editor.event-report-dialog
 * Controller: eventReportDialogCtrl
 */

import Assert from "tbAssert";

import angular from "angular";
import kendo from "@progress/kendo-ui";

import ngAnimate from "angular-animate";
import ngSanitize from "angular-sanitize";
import uiBootstrap from "angular-ui-bootstrap";
import kendoDirectives from "../../shims/kendoDirectives";


import EncodedHtmlDecoderServiceModule from "../../services/EncodedHtmlDecoderService";
import AllTrackingStatusesServiceModule from "../../services/AllTrackingStatusesService";

const reportDialogModes = Object.freeze({
    PreFile: "PreFile",
    File: "File",
    PostFile: "PostFile",
    ViewOnly: "ViewOnly"
});

const reportDialogRootElementId = "eventReportDialogModalRoot";

const reportDialogModeSet = new Set(Object.values(reportDialogModes));

const mReportEditorOptions = new WeakMap();

const mScopeSvc = new WeakMap();
const mSanitizeSvc = new WeakMap();
const mLogSvc = new WeakMap();
const mModalInstance = new WeakMap();
const mEncodedHtmlDecoderSvc = new WeakMap();
const mAllTrackingStatusesSvc = new WeakMap();

const mReportDialogMode = new WeakMap();
const mDialogTitle = new WeakMap();

const mSelectedReportStatus = new WeakMap();
const mCurrentReportResult = new WeakMap();
const mReportStatusEditable = new WeakMap();
const mResultHeaderLabelText = new WeakMap();
const mReportStatusChoices = new WeakMap();

const mShowNotifySubscribersOption = new WeakMap();
const mNotifySubscribersOfUpdate = new WeakMap();
const mDisableNotifySubscribersOption = new WeakMap();
const mCanSaveResult = new WeakMap();
const mSaveResultText = new WeakMap();

const mTrackingReportText = new WeakMap();

const mEventName = new WeakMap();
const mEventDates = new WeakMap();
const mEventAttendees = new WeakMap();

const eventReportDialogUserCancelledStatus = "cancel";

class EventReportDialogController {
    constructor($scope,
                $sanitize,
                $log,
                $uibModalInstance,
                EncodedHtmlDecoder,
                AllTrackingStatuses,
                eventInfo,
                statusEditMode,
                selectedResult,
                currentTrackingReport) {
        Assert.StrictAssert($scope != null, "Assertion Failure: Missing Scope Service Dependency");
        Assert.StrictAssert($sanitize != null, "Assertion Failure: Missing Sanitize Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert($uibModalInstance != null, "Assertion Failure: Missing Modal Instance Dependency");
        Assert.StrictAssert(EncodedHtmlDecoder != null, "Assertion Failure: Missing Encoded HTML Decoder Service Dependency");
        Assert.StrictAssert(AllTrackingStatuses != null, "Assertion Failure: Missing All Tracking Statuses Service Dependency");
        Assert.StrictAssert(eventInfo != null, "Assertion Failure: Missing Event Information Input Dependency");
        Assert.StrictAssert(statusEditMode != null, "Assertion Failure: Missing Status Edit Mode Input Dependency");
        Assert.StrictAssert(selectedResult != null, "Assertion Failure: Missing Selected Result Input Dependency");
        Assert.StrictAssert(currentTrackingReport != null, "Assertion Failure: Missing Current Tracking Report Input Dependency");

        mScopeSvc.set(this, $scope);
        mSanitizeSvc.set(this, $sanitize);
        mLogSvc.set(this, $log);
        mModalInstance.set(this, $uibModalInstance);
        mEncodedHtmlDecoderSvc.set(this, EncodedHtmlDecoder);
        mAllTrackingStatusesSvc.set(this, AllTrackingStatuses);

        if (reportDialogModeSet.has(statusEditMode.mode)) {
            mReportDialogMode.set(this, statusEditMode.mode);
        }
        else {
            mLogSvc.get(this).error("Invalid Report Dialog Mode Provided. Falling Back To View Only.");
            mReportDialogMode.set(this, reportDialogModes.ViewOnly);
        }

        mDialogTitle.set(this, null); //originally undefined.

        mSelectedReportStatus.set(this, undefined);
        mCurrentReportResult.set(this, undefined);
        mReportStatusEditable.set(this, false);
        mResultHeaderLabelText.set(this, undefined);
        mReportStatusChoices.set(this, []);

        mShowNotifySubscribersOption.set(this, false);
        mNotifySubscribersOfUpdate.set(this, false);
        mDisableNotifySubscribersOption.set(this, true);
        mCanSaveResult.set(this, false);
        mSaveResultText.set(this, undefined);

        mReportEditorOptions.set(this, {
            tools: [
                "formatting",
                "cleanFormatting",
                "bold",
                "italic",
                "underline",
                "insertUnorderedList",
                "insertOrderedList",
                "createLink",
                "unlink"
            ],
            resizable: false
        });

        switch (mReportDialogMode.get(this)) {
            case reportDialogModes.PreFile:
                this._setPreFileMode(currentTrackingReport);
                break;
            case reportDialogModes.File:
                this._setStandardReportingMode(selectedResult);
                break;
            case reportDialogModes.PostFile:
                this._setUpdateReportForAlreadyReportedEventMode(selectedResult, statusEditMode);
                break;
            default:
                this._setViewOnlyMode(selectedResult);
                break;
        }

        if (mCanSaveResult.get(this)) {
            mTrackingReportText.set(this, $sanitize(this.getDecodedTrackingReportText(currentTrackingReport)));
        }
        else {
            mTrackingReportText.set(this, "Tracking report is currently unavailable. Please contact your system administrator.");
        }

        mEventName.set(this, eventInfo.eventName);
        mEventDates.set(this, eventInfo.eventDates);
        mEventAttendees.set(this,
            eventInfo.eventAttendees.map(
                (tgt) => `${tgt.firstName} ${tgt.lastName}`
            ).join(", ")
        );

        $scope.$on("$destroy", () => {
            let dialogRootElement = angular.element(`#${reportDialogRootElementId}`);
            kendo.destroy(dialogRootElement);
        });
    }

    get reportDialogMode() {
        return mReportDialogMode.get(this);
    }

    get reportEditorOptions() {
        return mReportEditorOptions.get(this);
    }

    get dialogTitle() {
        return mDialogTitle.get(this);
    }

    get selectedReportStatus() {
        return mSelectedReportStatus.get(this);
    }

    set selectedReportStatus(updatedVal) {
        mSelectedReportStatus.set(this, updatedVal);
    }

    get currentReportResult() {
        return mCurrentReportResult.get(this);
    }

    get reportStatusEditable() {
        return mReportStatusEditable.get(this);
    }

    get resultHeaderLabelText() {
        return mResultHeaderLabelText.get(this);
    }

    get reportStatusChoices() {
        return mReportStatusChoices.get(this);
    }

    get showNotifySubscribersOption() {
        return mShowNotifySubscribersOption.get(this);
    }

    get notifySubscribersOfUpdate() {
        return mNotifySubscribersOfUpdate.get(this);
    }

    set notifySubscribersOfUpdate(updatedVal) {
        mNotifySubscribersOfUpdate.set(this, updatedVal);
    }

    get disableNotifySubscribersOption() {
        return mDisableNotifySubscribersOption.get(this);
    }

    get canSaveResult() {
        return mCanSaveResult.get(this);
    }

    get saveResultText() {
        return mSaveResultText.get(this);
    }

    get trackingReportText() {
        return mTrackingReportText.get(this);
    }

    set trackingReportText(updatedText) {
        mTrackingReportText.set(this, updatedText);
    }

    get eventName() {
        return mEventName.get(this);
    }

    get eventDates() {
        return mEventDates.get(this);
    }

    get eventAttendees() {
        return mEventAttendees.get(this);
    }

    _setPreFileMode(currentTrackingReport) {
        mDialogTitle.set(this, "Pre-File Tracking Report");
        mResultHeaderLabelText.set(this, "Pre-Filed Event Status: ");

        mReportStatusEditable.set(this, true);
        Array.prototype.push.apply(
            this.reportStatusChoices,
            mAllTrackingStatusesSvc.get(this).getReportedStatuses()
        );

        if (currentTrackingReport.preFiledStatus != null) {
            mSelectedReportStatus.set(this, currentTrackingReport.preFiledStatus.trackingStatusId);
            mCurrentReportResult.set(this, currentTrackingReport.preFiledStatus.trackingStatusName);
        }
        else {
            mSelectedReportStatus.set(this, -1);
            mCurrentReportResult.set(this, "None currently set");
        }

        mShowNotifySubscribersOption.set(this, false);
        mNotifySubscribersOfUpdate.set(this, null);

        mSaveResultText.set(this, "Pre-File Report");

        if (currentTrackingReport.serverHasReport === true) {//false or undefined.
            mDialogTitle.set(this, "Update Pre-Filed Tracking Report");
            mSaveResultText.set(this, "Save Changes");
        }

        mCanSaveResult.set(this, true);
    }

    _setStandardReportingMode(selectedResult) {
        mDialogTitle.set(this, "Report Event");
        mReportStatusEditable.set(this, false);
        mSelectedReportStatus.set(this, undefined);
        mResultHeaderLabelText.set(this, "Event Result: ");
        mCurrentReportResult.set(this, selectedResult);
        mShowNotifySubscribersOption.set(this, false);
        mNotifySubscribersOfUpdate.set(this, null);
        mSaveResultText.set(this, "File Report");
        mCanSaveResult.set(this, true);
    }

    _setUpdateReportForAlreadyReportedEventMode(selectedResult, statusEditMode) {
        mDialogTitle.set(this, "Update Tracking Report");
        mReportStatusEditable.set(this, false);
        mSelectedReportStatus.set(this, undefined);
        mResultHeaderLabelText.set(this, "Event Result: ");
        mCurrentReportResult.set(this, selectedResult);
        mShowNotifySubscribersOption.set(this, true);
        mNotifySubscribersOfUpdate.set(this, false);
        mDisableNotifySubscribersOption.set(this, (!(statusEditMode.canNotifySubscribers === true)));
        mSaveResultText.set(this, "Update Report");
        mCanSaveResult.set(this, true);
    }

    _setViewOnlyMode(selectedResult) {
        mDialogTitle.set(this, "Current Tracking Report");
        mResultHeaderLabelText.set(this, "Event Result: ");
        mCurrentReportResult.set(this, selectedResult);
        mReportStatusEditable.set(this, false);
        mCanSaveResult.set(this, false);
        mSaveResultText.set(this, "Read Only");
    }

    getDecodedTrackingReportText(reportData) {
        if (reportData.decodedReport == null) {
            reportData.decodedReport =
                mEncodedHtmlDecoderSvc.get(this).decode(reportData.report);
        }

        return reportData.decodedReport;
    }

    ok() {
        let trackingReportTextResult = this.trackingReportText;
        let selectedPreFileStatus = ((this.selectedReportStatus > 0) ?
                mAllTrackingStatusesSvc.get(this).getFromId(this.selectedReportStatus) :
                null
        );

        let notifySubscribersOption = this.notifySubscribersOfUpdate;

        let dialogResult = {
            updatedTrackingReport: trackingReportTextResult,
            updatedPreFileStatus: selectedPreFileStatus,
            notifySubscribers: notifySubscribersOption
        };

        mModalInstance.get(this).close(dialogResult);
    }

    cancel() {
        mModalInstance.get(this).dismiss(eventReportDialogUserCancelledStatus);
    }

}

EventReportDialogController.$inject = [
    "$scope",
    "$sanitize",
    "$log",
    "$uibModalInstance",
    "EncodedHtmlDecoder",
    "AllTrackingStatuses",
    "eventInfo",
    "statusEditMode",
    "selectedResult",
    "currentTrackingReport"
];

EventReportDialogController.dependencies = [
    ngAnimate,
    ngSanitize,
    uiBootstrap,
    kendoDirectives,
    EncodedHtmlDecoderServiceModule,
    AllTrackingStatusesServiceModule
];

EventReportDialogController.ReportDialogUserCancelledStatus = eventReportDialogUserCancelledStatus;

EventReportDialogController.template = require("./EventReportDialog.tpl.html");

EventReportDialogController.controllerAs = "eventReportDialog";

EventReportDialogController.ReportDialogModes = reportDialogModes;

export default EventReportDialogController;



//////////////////
// WEBPACK FOOTER
// ./src/views/tracking-event-editor/EventReportDialogController.js
// module id = B5gW
// module chunks = 1